import { Label, Rank } from '../enums';
import { Card, Entry } from '../types';
import { getHighCardRanks, getRankCounts, getRankIndex, isFlush, isStraight, isWheelStraight } from './helpers';

const HAND_TYPE_VALUES: Record<string, number> = {
  [Label.HIGH_CARD]: 0,
  [Label.ONE_PAIR]: 1,
  [Label.TWO_PAIR]: 2,
  [Label.THREE_OF_A_KIND]: 3,
  [Label.STRAIGHT]: 4,
  [Label.FLUSH]: 5,
  [Label.FULL_HOUSE]: 6,
  [Label.FOUR_OF_A_KIND]: 7,
  [Label.STRAIGHT_FLUSH]: 8,
  [Label.ROYAL_FLUSH]: 9,
};

function buildIndex(handType: Label, kickers: Rank[]): number {
  let index = HAND_TYPE_VALUES[handType] * 371293;
  for (let i = 0; i < kickers.length; i++) {
    index += getRankIndex(kickers[i]) * Math.pow(13, 4 - i);
  }
  return index;
}

export function evaluateHighHand(cards: readonly Card[]): Entry {
  if (cards.length !== 5) {
    throw new Error(`High hand requires exactly 5 cards, got ${cards.length}`);
  }

  const rankCounts = getRankCounts(cards);
  const flush = isFlush(cards);
  const straight = isStraight(cards);
  const wheel = isWheelStraight(cards);

  const counts = Array.from(rankCounts.entries()).sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return getRankIndex(b[0]) - getRankIndex(a[0]);
  });

  if (straight && flush) {
    const highRanks = getHighCardRanks(cards);
    if (
      highRanks[0] === Rank.ACE &&
      highRanks[1] === Rank.KING &&
      highRanks[2] === Rank.QUEEN &&
      highRanks[3] === Rank.JACK &&
      highRanks[4] === Rank.TEN
    ) {
      return {
        label: Label.ROYAL_FLUSH,
        index: buildIndex(Label.ROYAL_FLUSH, [Rank.ACE]),
      };
    }
    const kicker = wheel ? Rank.FIVE : highRanks[0];
    return {
      label: Label.STRAIGHT_FLUSH,
      index: buildIndex(Label.STRAIGHT_FLUSH, [kicker]),
    };
  }

  if (counts[0][1] === 4) {
    const quad = counts[0][0];
    const kicker = counts[1][0];
    return {
      label: Label.FOUR_OF_A_KIND,
      index: buildIndex(Label.FOUR_OF_A_KIND, [quad, kicker]),
    };
  }

  if (counts[0][1] === 3 && counts[1][1] === 2) {
    const trips = counts[0][0];
    const pair = counts[1][0];
    return {
      label: Label.FULL_HOUSE,
      index: buildIndex(Label.FULL_HOUSE, [trips, pair]),
    };
  }

  if (flush) {
    const kickers = getHighCardRanks(cards);
    return {
      label: Label.FLUSH,
      index: buildIndex(Label.FLUSH, kickers),
    };
  }

  if (straight) {
    const highRanks = getHighCardRanks(cards);
    const kicker = wheel ? Rank.FIVE : highRanks[0];
    return {
      label: Label.STRAIGHT,
      index: buildIndex(Label.STRAIGHT, [kicker]),
    };
  }

  if (counts[0][1] === 3) {
    const trips = counts[0][0];
    const kickers = counts.slice(1).map((c) => c[0]);
    return {
      label: Label.THREE_OF_A_KIND,
      index: buildIndex(Label.THREE_OF_A_KIND, [trips, ...kickers]),
    };
  }

  if (counts[0][1] === 2 && counts[1][1] === 2) {
    const highPair = counts[0][0];
    const lowPair = counts[1][0];
    const kicker = counts[2][0];
    return {
      label: Label.TWO_PAIR,
      index: buildIndex(Label.TWO_PAIR, [highPair, lowPair, kicker]),
    };
  }

  if (counts[0][1] === 2) {
    const pair = counts[0][0];
    const kickers = counts.slice(1).map((c) => c[0]);
    return {
      label: Label.ONE_PAIR,
      index: buildIndex(Label.ONE_PAIR, [pair, ...kickers]),
    };
  }

  const kickers = getHighCardRanks(cards);
  return {
    label: Label.HIGH_CARD,
    index: buildIndex(Label.HIGH_CARD, kickers),
  };
}
